/**
 * Color Lookup Table (LUT) Service
 * Uses Cornerstone3D native viewport.setProperties({ colormap }) API
 *
 * Cornerstone3D lookup chain:
 *   1. Custom registry (utilities.colormap.registerColormap)
 *   2. VTK.js built-in presets (vtkColorMaps.getPresetByName)
 *
 * The colormap object passed to setProperties follows ColormapPublic interface:
 *   { name: string, opacity?: number | OpacityMapping[] }
 *
 * PET/CT Fusion:
 *   Overlays a PET series onto the active CT viewport as a colorized actor.
 *   Uses the heatmap overlay pattern (createActorMapper + addActor) with
 *   VTK.js color transfer functions for LUT-based colorization.
 */

import { cache, CONSTANTS, Enums, metaData, utilities } from "@cornerstonejs/core";
import { loadAndCacheImage } from "@cornerstonejs/core/loaders/imageLoader";
import { resolvePrimaryFrameImageId } from "./MultiFrameService";
import vtkColorTransferFunction from "@kitware/vtk.js/Rendering/Core/ColorTransferFunction";
import vtkColorMaps from "@kitware/vtk.js/Rendering/Core/ColorTransferFunction/ColorMaps";
import vtkImageDataFactory from "@kitware/vtk.js/Common/DataModel/ImageData";
import vtkDataArrayFactory from "@kitware/vtk.js/Common/Core/DataArray";

// ─── Custom colormap definitions ───────────────────────────────────────────────
// Medical imaging and MATLAB-style palettes not available as VTK.js built-ins.
// Registered at module load so they work in both viewport.setProperties() and
// the vtkColorTransferFunction fallback inside applyPETFusion().
//
// RGBPoints format: flat array [x, r, g, b, x, r, g, b, …] where x and r/g/b are all 0-1.
const _CUSTOM_COLORMAP_DEFS = [
  {
    Name: "Hot Iron",
    ColorSpace: "RGB",
    // Black → dark-red → red → orange → yellow → white
    RGBPoints: [
      0.000, 0.000, 0.000, 0.000,
      0.333, 0.502, 0.000, 0.000,
      0.500, 1.000, 0.000, 0.000,
      0.667, 1.000, 0.502, 0.000,
      0.833, 1.000, 1.000, 0.000,
      1.000, 1.000, 1.000, 1.000,
    ],
  },
  {
    Name: "PET",
    ColorSpace: "RGB",
    // Black → blue → cyan → green → yellow → orange → red → white
    RGBPoints: [
      0.000, 0.000, 0.000, 0.000,
      0.100, 0.000, 0.000, 0.400,
      0.200, 0.000, 0.000, 0.800,
      0.300, 0.000, 0.400, 0.800,
      0.400, 0.000, 0.800, 0.400,
      0.500, 0.000, 0.800, 0.000,
      0.600, 0.800, 0.800, 0.000,
      0.700, 1.000, 0.600, 0.000,
      0.800, 1.000, 0.200, 0.000,
      0.900, 0.900, 0.000, 0.000,
      1.000, 1.000, 1.000, 1.000,
    ],
  },
  {
    Name: "Hot Metal Blue",
    ColorSpace: "RGB",
    // Black → dark-blue → purple → red → orange → yellow
    RGBPoints: [
      0.000, 0.000, 0.000, 0.000,
      0.200, 0.000, 0.000, 0.502,
      0.400, 0.502, 0.000, 0.502,
      0.600, 1.000, 0.000, 0.000,
      0.800, 1.000, 0.502, 0.000,
      1.000, 1.000, 1.000, 0.000,
    ],
  },
  {
    Name: "PET 20 Step",
    ColorSpace: "RGB",
    // 20 visually distinct bands — each control point marks the centre of one band
    RGBPoints: [
      0.000, 0.000, 0.000, 0.000,
      0.050, 0.000, 0.000, 0.400,
      0.100, 0.000, 0.000, 0.800,
      0.150, 0.000, 0.400, 1.000,
      0.200, 0.000, 0.800, 1.000,
      0.250, 0.000, 0.800, 0.600,
      0.300, 0.000, 0.800, 0.000,
      0.350, 0.400, 1.000, 0.000,
      0.400, 0.800, 1.000, 0.000,
      0.450, 1.000, 1.000, 0.000,
      0.500, 1.000, 0.800, 0.000,
      0.550, 1.000, 0.600, 0.000,
      0.600, 1.000, 0.400, 0.000,
      0.650, 1.000, 0.200, 0.000,
      0.700, 1.000, 0.000, 0.000,
      0.750, 0.800, 0.000, 0.000,
      0.800, 0.600, 0.000, 0.200,
      0.850, 0.800, 0.400, 0.600,
      0.900, 1.000, 0.700, 0.800,
      0.950, 1.000, 1.000, 1.000,
      1.000, 1.000, 1.000, 1.000,
    ],
  },
  {
    Name: "Spring",
    ColorSpace: "RGB",
    // Black (background stays unlit, matches Hot Iron/PET/Hot Metal Blue) → Magenta → Yellow (MATLAB spring)
    RGBPoints: [
      0.000, 0.000, 0.000, 0.000,
      0.150, 1.000, 0.000, 1.000,
      1.000, 1.000, 1.000, 0.000,
    ],
  },
  {
    Name: "Summer",
    ColorSpace: "RGB",
    // Black (background stays unlit) → Green-teal → Yellow-green with constant 0.4 blue (MATLAB summer)
    RGBPoints: [
      0.000, 0.000, 0.000, 0.000,
      0.150, 0.000, 0.502, 0.400,
      1.000, 1.000, 1.000, 0.400,
    ],
  },
  {
    Name: "Fall",
    ColorSpace: "RGB",
    // Black (background stays unlit) → Red → Yellow (MATLAB autumn/fall)
    RGBPoints: [
      0.000, 0.000, 0.000, 0.000,
      0.150, 1.000, 0.000, 0.000,
      1.000, 1.000, 1.000, 0.000,
    ],
  },
  {
    Name: "Winter",
    ColorSpace: "RGB",
    // Black (background stays unlit) → Blue → Green-blue (MATLAB winter)
    RGBPoints: [
      0.000, 0.000, 0.000, 0.000,
      0.150, 0.000, 0.000, 1.000,
      1.000, 0.000, 1.000, 0.500,
    ],
  },
  {
    Name: "Rainbow",
    ColorSpace: "RGB",
    // Black (background stays unlit) → blue → cyan → green → yellow → red
    RGBPoints: [
      0.000, 0.000, 0.000, 0.000,
      0.150, 0.000, 0.000, 1.000,
      0.350, 0.000, 1.000, 1.000,
      0.550, 0.000, 1.000, 0.000,
      0.750, 1.000, 1.000, 0.000,
      1.000, 1.000, 0.000, 0.000,
    ],
  },
];

const _DICOM_PALETTE_SOURCES = {
  "Hot Iron": "hotIron",
  "PET": "pet",
  "Hot Metal Blue": "hotMetalBlue",
  "PET 20 Step": "pet20Step",
};

/** Display name -> Uint8Array(256 * 3), for palettes we hold exactly. */
const _EXACT_PALETTES = new Map();

for (const [name, key] of Object.entries(_DICOM_PALETTE_SOURCES)) {
  const colors = CONSTANTS?.CPU_COLORMAPS?.[key]?.colors;
  if (!Array.isArray(colors) || colors.length !== 256) {
    console.warn(
      `[Colormap] No exact DICOM table for "${name}"; using the approximate definition`
    );
    continue;
  }

  const table = new Uint8Array(256 * 3);
  const rgbPoints = [];
  for (let i = 0; i < 256; i++) {
    const [red, green, blue] = colors[i];
    table[i * 3] = red;
    table[i * 3 + 1] = green;
    table[i * 3 + 2] = blue;
    // One control point per entry, so anything reading this through a transfer
    // function reproduces the table rather than interpolating across it.
    rgbPoints.push(i / 255, red / 255, green / 255, blue / 255);
  }

  _EXACT_PALETTES.set(name, table);
  _CUSTOM_COLORMAP_DEFS.push({ Name: name, ColorSpace: "RGB", RGBPoints: rgbPoints });
}

_CUSTOM_COLORMAP_DEFS.forEach((cm) => {
  try {
    utilities.colormap.registerColormap(cm);
  } catch { /* already registered — safe to ignore */ }
});

// ─── Available colormap lists ───────────────────────────────────────────────────
// Keys are the display names shown in the UI; values are the names used to look up
// the colormap (VTK.js built-in preset or Cornerstone custom registry).
// Object.values()[0] is used as the default, so list the desired default first.

/**
 * General-purpose colormaps for the Color LUT tool.
 * VTK.js built-ins are used where possible; custom-registered ones fill the rest.
 */
export const COLORMAPS = {
  "Hot Iron":       "Hot Iron",
  "PET":            "PET",
  "Hot Metal Blue": "Hot Metal Blue",
  "PET 20 Step":    "PET 20 Step",
  "Rainbow":        "Rainbow",
  "Spring":         "Spring",
  "Summer":         "Summer",
  "Fall":           "Fall",
  "Winter":         "Winter",
};

/**
 * Check if viewport is a StackViewport
 */
const isStackViewport = (viewport) => {
  return viewport && viewport.type === Enums.ViewportType.STACK;
};

/**
 * Check if viewport is a VolumeViewport (3D or orthographic)
 */
const isVolumeViewport = (viewport) => {
  return (
    viewport &&
    (viewport.type === Enums.ViewportType.VOLUME_3D ||
      viewport.type === Enums.ViewportType.ORTHOGRAPHIC)
  );
};

/**
 * Apply a colormap to a single viewport using Cornerstone3D native APIs.
 * Follows the OHIF setViewportColormap pattern:
 *   - StackViewport: viewport.setProperties({ colormap })
 *   - VolumeViewport: viewport.setProperties({ colormap }, volumeId)
 *
 * @param {object} viewport - Cornerstone3D viewport instance
 * @param {string} colormapName - VTK.js preset name (from COLORMAPS)
 * @param {object} options - { opacity, invert }
 * @returns {boolean} true if successfully applied
 */
export const applyColormapToViewport = (viewport, colormapName, options = {}) => {
  const { opacity = 1 } = options;

  const colormap = {
    name: colormapName,
  };

  try {
    if (isStackViewport(viewport)) {
      // Apply the colormap (StackViewport ignores opacity in colormap object)
      viewport.setProperties({ colormap });

      // Set opacity directly on the VTK actor since StackViewport.setColormapGPU
      // does not handle the colormap.opacity property
      const actorEntry = viewport.getDefaultActor?.();
      if (actorEntry?.actor) {
        const property = actorEntry.actor.getProperty?.();
        if (property?.setOpacity) {
          property.setOpacity(opacity);
        }
      }

      viewport.render();
      return true;
    }

    if (isVolumeViewport(viewport)) {
      // VolumeViewport handles OpacityMapping[] natively in BaseVolumeViewport
      const opacityMapping = [
        { value: 0, opacity },
        { value: 1, opacity },
      ];
      colormap.opacity = opacityMapping;

      const volumeId = viewport.getAllVolumeIds?.()?.[0] ?? viewport.getVolumeId?.();
      viewport.setProperties({ colormap }, volumeId);
      viewport.render();
      return true;
    }

    console.warn("Unsupported viewport type for colormap:", viewport.type);
    return false;
  } catch (error) {
    console.error("Error applying colormap to viewport:", error);
    return false;
  }
};

/**
 * Reset (remove) colormap from a single viewport.
 * Sets invert to false and resets properties to default grayscale rendering.
 *
 * @param {object} viewport - Cornerstone3D viewport instance
 * @returns {boolean} true if successfully reset
 */
export const resetColormapOnViewport = (viewport) => {
  try {
    if (isStackViewport(viewport)) {
      // Restore actor opacity to full before resetting properties
      const actorEntry = viewport.getDefaultActor?.();
      if (actorEntry?.actor) {
        const property = actorEntry.actor.getProperty?.();
        if (property?.setOpacity) {
          property.setOpacity(1);
        }
      }
      // resetProperties restores default rendering (removes colormap transfer function)
      viewport.resetProperties?.();
      viewport.render();
      return true;
    }

    if (isVolumeViewport(viewport)) {
      const volumeId = viewport.getAllVolumeIds?.()?.[0] ?? viewport.getVolumeId?.();
      viewport.resetProperties?.(volumeId);
      viewport.render();
      return true;
    }

    return false;
  } catch (error) {
    console.error("Error resetting colormap on viewport:", error);
    return false;
  }
};

/**
 * Apply colormap to all provided viewports.
 *
 * @param {Array} viewports - Array of Cornerstone3D viewport instances
 * @param {string} colormapName - VTK.js preset name
 * @param {object} options - { opacity }
 * @returns {number} count of successfully processed viewports
 */
export const applyColormapToViewports = (viewports, colormapName, options = {}) => {
  let processedCount = 0;

  for (const viewport of viewports) {
    const success = applyColormapToViewport(viewport, colormapName, options);
    if (success) processedCount++;
  }

  return processedCount;
};

/**
 * Reset colormap on all provided viewports.
 *
 * @param {Array} viewports - Array of Cornerstone3D viewport instances
 * @returns {number} count of successfully reset viewports
 */
export const resetColormapOnViewports = (viewports) => {
  let resetCount = 0;

  for (const viewport of viewports) {
    const success = resetColormapOnViewport(viewport);
    if (success) resetCount++;
  }

  return resetCount;
};

/**
 * Register a custom colormap with Cornerstone3D.
 * Use this for colormaps not provided by VTK.js built-in presets.
 *
 * @param {string} name - Unique colormap name
 * @param {string} colorSpace - Color space (e.g. "RGB", "Diverging")
 * @param {number[]} rgbPoints - Flat array of [x, r, g, b, ...] control points
 *                                x is normalized 0-1, r/g/b are 0-1
 */
export const registerCustomColormap = (name, colorSpace, rgbPoints) => {
  utilities.colormap.registerColormap({
    Name: name,
    ColorSpace: colorSpace,
    RGBPoints: rgbPoints,
  });
};

/**
 * Get list of registered custom colormap names.
 * @returns {string[]}
 */
export const getRegisteredColormapNames = () => {
  return utilities.colormap.getColormapNames?.() ?? [];
};

// ─── PET/CT Fusion Service ──────────────────────────────────────────────────

const PET_FUSION_ACTOR_UID = "pet-fusion-overlay";

/**
 * Recommended colormaps for PET fusion.
 * Hot Iron is first so it becomes the default when the overlay opens.
 */
export const PET_COLORMAPS = {
  "Hot Iron":       "Hot Iron",
  "PET":            "PET",
  "Hot Metal Blue": "Hot Metal Blue",
  "PET 20 Step":    "PET 20 Step",
  "Rainbow":        "Rainbow",
  "Spring":         "Spring",
  "Summer":         "Summer",
  "Fall":           "Fall",
  "Winter":         "Winter",
};

/** Evenly spaced slices loaded once to measure a series (see _getSeriesProfile). */
const SERIES_PROFILE_SAMPLES = 8;

/**
 * Overlay slices read in parallel while warming a series.
 */
const OVERLAY_PREFETCH_CONCURRENCY = 6;

/**
 * Reads in flight while pulling both series down up front (prepareFusionSeries).
 */
const FUSION_PREPARE_CONCURRENCY = 16;

/**
 * Series that cannot be fused whatever their study: they carry no pixel data.
 * Mirrors NON_IMAGE_MODALITIES in the hanging-protocol helper.
 */
const NON_FUSABLE_MODALITIES = new Set(["PR", "SR", "KO", "DOC"]);

/**
 * Top of the display window for a PET overlay carrying SUV scaling, in SUV
 * body-weight units.
 *
 * Cornerstone pre-scales PT pixel data through scaleArray, which for a series
 * carrying SUVbw computes `suvbw * (stored * slope + intercept)` — so what
 * getPixelData() returns is already SUV, and a window can be stated in the
 * units clinicians read. SUV 0-5 is the conventional display window for FDG.
 *
 * Windowing to the measured maximum instead is what made the overlay wash out:
 * a whole-body PET almost always contains a focus far hotter than anything
 * diagnostic — bladder, brain, injection site, often SUV 30-100 — and dividing
 * by that leaves ordinary uptake in the bottom few percent of the colormap,
 * which is its near-black end. Clipping above the window is what gives the
 * bright end of the ramp something to show.
 */
const SUV_WINDOW_TOP = 5;

/**
 * Below this the fixed SUV window is not believable — a tracer or a scaling
 * this code should not assume anything about — so the measured path is used.
 */
const SUV_MIN_CREDIBLE_MAX = 1;

/** Resolution of the histogram the measured window's top is read off. */
const HISTOGRAM_BINS = 512;

/**
 * Where the measured window's top sits in the sampled voxel distribution.
 * Used when there is no SUV scale to window against (NM, or a CT/MR overlay).
 * A percentile rather than the maximum, for the same reason as above: one hot
 * voxel must not decide the scale for the whole series.
 */
const WINDOW_TOP_PERCENTILE = 0.99;

/** How closely the overlay's in-plane axes must match the base image's. */
const DIRECTION_EPSILON = 1e-3;

const _colormapLUTCache = new Map();   // colormapName -> Uint8Array(256 * 3)
const _seriesProfileCache = new Map(); // series + scan axis -> profile

const _profileKey = (imageIds, normal) =>
  `${_seriesKey(imageIds)}::${normal.map((c) => c.toFixed(4)).join(",")}`;

const _dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

const _seriesKey = (imageIds) => `${imageIds.length}::${imageIds[0]}`;

/**
 * Modality-LUT rescale that turns stored pixel values into real units.
 */
const _rescaleOf = (image) => {
  if (image?.preScale?.scaled) return { slope: 1, intercept: 0 };
  return {
    slope: Number.isFinite(image?.slope) ? image.slope : 1,
    intercept: Number.isFinite(image?.intercept) ? image.intercept : 0,
  };
};

/** Rescaled [min, max] of one loaded image, or null if it has no pixels. */
const _rescaledRangeOf = (image) => {
  const { slope, intercept } = _rescaleOf(image);
  let low = image?.minPixelValue;
  let high = image?.maxPixelValue;

  if (!Number.isFinite(low) || !Number.isFinite(high)) {
    const pixelData = image?.getPixelData?.();
    if (!pixelData?.length) return null;
    low = Infinity;
    high = -Infinity;
    for (let i = 0; i < pixelData.length; i++) {
      if (pixelData[i] < low) low = pixelData[i];
      if (pixelData[i] > high) high = pixelData[i];
    }
  }

  return [low * slope + intercept, high * slope + intercept];
};

/**
 * Fit slice location against slice index over the sampled slices.
 *
 * @returns {{origin:number, step:number}|null}
 */
const _fitSliceLocations = (locations) => {
  const n = locations.length;
  if (n < 2) return null;

  let sumIndex = 0;
  let sumLocation = 0;
  let sumIndexSquared = 0;
  let sumProduct = 0;
  for (const { index, location } of locations) {
    sumIndex += index;
    sumLocation += location;
    sumIndexSquared += index * index;
    sumProduct += index * location;
  }

  const denominator = n * sumIndexSquared - sumIndex * sumIndex;
  if (!denominator) return null;

  const step = (n * sumProduct - sumIndex * sumLocation) / denominator;
  const origin = (sumLocation - step * sumIndex) / n;
  if (!Number.isFinite(step) || !Number.isFinite(origin) || step === 0) return null;

  // Half a slice is the most a sample may sit off the line before pairing would
  // start picking the neighbouring slice.
  const tolerance = Math.abs(step) / 2;
  for (const { index, location } of locations) {
    if (Math.abs(origin + step * index - location) > tolerance) return null;
  }

  return { origin, step };
};

/**
 * True when Cornerstone has already turned this image's pixels into SUV — PT
 * modality with an SUVbw factor, pre-scaled by the loader.
 */
const _isSuvScaled = (image) =>
  !!(image?.preScale?.scaled && image?.preScale?.scalingParameters?.suvbw);

/**
 * Value below which `fraction` of the sampled voxels fall, or null if it cannot
 * be measured. Built from a histogram rather than a sort, so the cost stays
 * linear in the pixels examined.
 */
const _percentileOf = (samples, low, high, fraction) => {
  if (!(high > low) || !samples.length) return null;

  const histogram = new Uint32Array(HISTOGRAM_BINS);
  const scale = (HISTOGRAM_BINS - 1) / (high - low);
  let total = 0;

  for (const { image, slope, intercept } of samples) {
    const pixelData = image?.getPixelData?.();
    if (!pixelData?.length) continue;
    for (let i = 0; i < pixelData.length; i++) {
      let bin = ((pixelData[i] * slope + intercept - low) * scale) | 0;
      if (bin < 0) bin = 0;
      else if (bin >= HISTOGRAM_BINS) bin = HISTOGRAM_BINS - 1;
      histogram[bin]++;
      total++;
    }
  }
  if (!total) return null;

  const cutoff = total * fraction;
  let seen = 0;
  for (let bin = 0; bin < HISTOGRAM_BINS; bin++) {
    seen += histogram[bin];
    if (seen >= cutoff) return low + (bin + 1) / scale;
  }
  return null;
};

/**
 * The window the overlay is colorized against.
 *
 * Stated in SUV when the series carries an SUV scale, because that is a real
 * unit with a conventional display range; measured from the data otherwise.
 *
 * @returns {{windowLow: number, windowHigh: number, basis: string}}
 */
const _displayWindowFor = (samples, min, max) => {
  if (samples.some(({ image }) => _isSuvScaled(image)) && max >= SUV_MIN_CREDIBLE_MAX) {
    return { windowLow: 0, windowHigh: SUV_WINDOW_TOP, basis: "suv" };
  }

  const measuredLow = Number.isFinite(min) ? min : 0;
  const measuredHigh = Number.isFinite(max) ? max : 1;
  const percentileHigh = _percentileOf(
    samples,
    measuredLow,
    measuredHigh,
    WINDOW_TOP_PERCENTILE
  );

  return percentileHigh != null && percentileHigh > measuredLow
    ? { windowLow: measuredLow, windowHigh: percentileHigh, basis: "percentile" }
    : { windowLow: measuredLow, windowHigh: measuredHigh, basis: "measured" };
};

/**
 * 256-entry colour table for a colormap, plus the alpha weight each entry earns.
 *
 * @returns {Uint8Array} 256 * 3 bytes
 */
const _sampleColormap = (colormapName) => {
  const cfun = vtkColorTransferFunction.newInstance();
  const preset = vtkColorMaps.getPresetByName(colormapName);
  const csColormap = preset ? null : utilities.colormap.getColormap?.(colormapName);

  if (preset) {
    cfun.applyColorMap(preset);
  } else if (csColormap) {
    cfun.applyColorMap(csColormap);
  } else {
    // Default hot-like fallback
    cfun.addRGBPoint(0.0, 0, 0, 0);
    cfun.addRGBPoint(0.25, 0.5, 0, 0);
    cfun.addRGBPoint(0.5, 1, 0.5, 0);
    cfun.addRGBPoint(0.75, 1, 1, 0);
    cfun.addRGBPoint(1.0, 1, 1, 1);
  }
  cfun.setMappingRange(0, 1);

  const rgb = new Uint8Array(256 * 3);
  const colour = [0, 0, 0];
  for (let i = 0; i < 256; i++) {
    cfun.getColor(i / 255, colour);
    rgb[i * 3] = Math.round(colour[0] * 255);
    rgb[i * 3 + 1] = Math.round(colour[1] * 255);
    rgb[i * 3 + 2] = Math.round(colour[2] * 255);
  }
  return rgb;
};

const _getColormapLUT = (colormapName) => {
  const cached = _colormapLUTCache.get(colormapName);
  if (cached) return cached;

  const rgb = _EXACT_PALETTES.get(colormapName) ?? _sampleColormap(colormapName);
  _colormapLUTCache.set(colormapName, rgb);
  return rgb;
};

/**
 * Measure an overlay series once: where its slices sit along the base
 * viewport's scan axis, and the intensity range to colorize against.
 * @returns {{windowLow:number, windowHigh:number, windowBasis:string,
 *            locationOrigin:number|null, locationStep:number|null,
 *            sharesPatientSpace:boolean, located:boolean}}
 */
const _getSeriesProfile = async (imageIds, normal, baseFrameOfReferenceUID) => {
  const key = _profileKey(imageIds, normal);
  const cached = _seriesProfileCache.get(key);
  if (cached) return cached;

  const count = imageIds.length;
  const sampleCount = Math.min(SERIES_PROFILE_SAMPLES, count);
  const sampleIndices = [];
  for (let i = 0; i < sampleCount; i++) {
    const index =
      sampleCount === 1 ? 0 : Math.round((i * (count - 1)) / (sampleCount - 1));
    if (!sampleIndices.includes(index)) sampleIndices.push(index);
  }

  const samples = [];
  const locations = [];
  let min = Infinity;
  let max = -Infinity;
  let sharesPatientSpace = true;

  // Read the samples together, not one after another. Fusion cannot start
  // until this finishes, so a sequential loop here is SERIES_PROFILE_SAMPLES
  // round trips of dead time on the very click that asks for fusion — and at
  // Interaction priority, because the user is waiting on it rather than it
  // being background work.
  const sampled = await Promise.all(
    sampleIndices.map(async (index) => {
      try {
        return {
          index,
          image: await loadAndCacheImage(resolvePrimaryFrameImageId(imageIds[index]), {
            requestType: Enums.RequestType.Interaction,
          }),
        };
      } catch (error) {
        console.warn("[PET Fusion] Could not sample overlay slice", index, error);
        return { index, image: null };
      }
    })
  );

  for (const { index, image } of sampled) {
    if (!image) continue;

    const { slope, intercept } = _rescaleOf(image);
    samples.push({ image, slope, intercept });

    const range = _rescaledRangeOf(image);
    if (range) {
      if (range[0] < min) min = range[0];
      if (range[1] > max) max = range[1];
    }

    const meta = utilities.getImageDataMetadata?.(image);
    const frameOfReferenceUID = meta?.imagePlaneModule?.frameOfReferenceUID;
    if (
      baseFrameOfReferenceUID &&
      frameOfReferenceUID &&
      frameOfReferenceUID !== baseFrameOfReferenceUID
    ) {
      // Different frame of reference — the two series' patient coordinates are
      // not comparable, so nothing below can be trusted to align them.
      sharesPatientSpace = false;
    }

    if (Array.isArray(meta?.origin) && meta.origin.length === 3) {
      locations.push({ index, location: _dot(meta.origin, normal) });
    }
  }

  // A negative step just means the two series are ordered head-first vs
  // feet-first, which pairing handles; a null model means pairing has to fall
  // back to slice index.
  const sliceModel = _fitSliceLocations(locations);
  if (!sliceModel && locations.length >= 2) {
    console.warn(
      "[PET Fusion] Overlay slice positions are not evenly ordered; falling back to index pairing"
    );
  }

  const { windowLow, windowHigh, basis } = _displayWindowFor(samples, min, max);

  const profile = {
    windowLow,
    windowHigh,
    windowBasis: basis,
    locationOrigin: sliceModel ? sliceModel.origin : null,
    locationStep: sliceModel ? sliceModel.step : null,
    sharesPatientSpace,
    located: locations.length > 0,
  };

  _seriesProfileCache.set(key, profile);
  return profile;
};

// One warming run per overlay series, so repeated slice changes re-aim the run
// in flight rather than starting another one.
const _prefetchRuns = new Map(); // series key -> run

const _overlayImages = new Map(); // series key -> Map<imageId, image>

const _imagesFor = (imageIds) => {
  const key = _seriesKey(imageIds);
  let images = _overlayImages.get(key);
  if (!images) {
    images = new Map();
    _overlayImages.set(key, images);
  }
  return images;
};

/**
 * Read one overlay slice and remember it.
 */
const _readOverlayImage = async (imageIds, imageId, requestType, priority = 0) => {
  // A stack's imageId is not always the id that can actually be loaded: for an
  // un-split multi-frame instance the frame-less base URL can hang the wadouri
  // loader. The viewer's own prefetcher resolves it the same way.
  const image = await loadAndCacheImage(resolvePrimaryFrameImageId(imageId), {
    requestType,
    priority,
  });
  if (image) _imagesFor(imageIds).set(imageId, image);
  return image;
};

/**
 * Warm the whole overlay series in the background, nearest slice to `focus`
 * first, so that wherever a scroll lands the image is already in hand.
 */
const _warmOverlaySeries = (imageIds, focus) => {
  const key = _seriesKey(imageIds);
  const running = _prefetchRuns.get(key);
  if (running) {
    running.focus = focus;
    return;
  }

  const pending = new Set();
  for (let i = 0; i < imageIds.length; i++) pending.add(i);

  const run = { focus, pending, cancelled: false };
  _prefetchRuns.set(key, run);

  const takeNearest = () => {
    let nearest = null;
    let nearestDistance = Infinity;
    for (const index of run.pending) {
      const distance = Math.abs(index - run.focus);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = index;
      }
    }
    if (nearest !== null) run.pending.delete(nearest);
    return nearest;
  };

  const readAhead = async () => {
    while (!run.cancelled) {
      const index = takeNearest();
      if (index === null) break;
      const imageId = imageIds[index];
      if (!imageId) continue;
      try {
        if (cache?.isLoaded?.(imageId)) continue;
        await _readOverlayImage(imageIds, imageId, Enums.RequestType.Prefetch, 1);
      } catch { /* the on-demand read will surface it */ }
    }
    if (!run.pending.size || run.cancelled) _prefetchRuns.delete(key);
  };

  const workers = [];
  for (let i = 0; i < OVERLAY_PREFETCH_CONCURRENCY; i++) workers.push(readAhead());
  run.finished = Promise.all(workers);
};

/**
 * Read every slice of both series, reporting progress as it goes.
 *
 * @param {object} series - { overlayImageIds, baseImageIds }
 * @param {(read: number, total: number) => void} [onProgress] - never allowed
 *   to break the run; a throw from it is reported and swallowed.
 * @returns {{ done: Promise<void>, cancel: () => void }}
 */
export const prepareFusionSeries = ({ overlayImageIds, baseImageIds }, onProgress) => {
  const targets = [];
  for (const imageId of overlayImageIds ?? []) {
    if (imageId) targets.push({ imageId, imageIds: overlayImageIds });
  }
  for (const imageId of baseImageIds ?? []) {
    if (imageId) targets.push({ imageId, imageIds: null });
  }

  const total = targets.length;
  const run = { cancelled: false };
  const cancel = () => {
    run.cancelled = true;
  };

  const report = (read, of) => {
    try {
      onProgress?.(read, of);
    } catch (error) {
      // An observer that throws must not abandon a half-read study.
      console.warn("[PET Fusion] Progress observer threw", error);
    }
  };

  if (!total) {
    report(0, 0);
    return { done: Promise.resolve(), cancel };
  }

  let read = 0;
  let next = 0;

  const readAll = async () => {
    while (!run.cancelled && next < total) {
      const { imageId, imageIds } = targets[next++];
      try {
        if (!cache?.isLoaded?.(imageId)) {
          if (imageIds) {
            await _readOverlayImage(imageIds, imageId, Enums.RequestType.Prefetch, 1);
          } else {
            await loadAndCacheImage(resolvePrimaryFrameImageId(imageId), {
              requestType: Enums.RequestType.Prefetch,
              priority: 1,
            });
          }
        }
      } catch {
        // A slice that will not read is not worth holding the whole study for.
      }
      read++;
      if (!run.cancelled) report(read, total);
    }
  };

  report(0, total);
  const workers = [];
  const concurrency = Math.min(FUSION_PREPARE_CONCURRENCY, total);
  for (let i = 0; i < concurrency; i++) workers.push(readAll());

  return { done: Promise.all(workers).then(() => undefined), cancel };
};

/**
 * Stop warming an overlay series. Worth calling when fusion is removed: the run
 * would otherwise keep reading a series nothing is going to display, competing
 * with the base series for the connection.
 */
export const stopOverlayPrefetch = (imageIds) => {
  if (!imageIds?.length) return;
  const key = _seriesKey(imageIds);
  const run = _prefetchRuns.get(key);
  if (run) {
    run.cancelled = true;
    _prefetchRuns.delete(key);
  }
  // Drop the strong references so Cornerstone's cache can evict the series.
  _overlayImages.delete(key);
};

// Slice changes arriving faster than the overlay can be built would otherwise
// race: a slow read for an earlier slice can land after a fast one for a later
// slice and leave the wrong overlay on screen. Each call takes a ticket, and
// only the newest is allowed to touch the actor.
const _applyTickets = new Map(); // viewport id -> latest ticket

/**
 * The series that can be fused onto the one the active viewport is showing.
 *
 * Which study to read them from is not a given. On the ordinary route a study's
 * series live in `studyData.stacks`; on the priors route `studyData` stays empty
 * by design and every study's data lives in `priorStudyData` ([0] = current,
 * [1..n] = priors). Reading only the former leaves the picker empty there.
 *
 * Series are taken from the study the active viewport is displaying, matched by
 * the stack it is showing rather than by position, so a viewport filled from a
 * prior offers that prior's series. Fusion stays within one study: a series from
 * another study shares no frame of reference with the base image, and would be
 * stretched over it rather than registered to it.
 *
 * @param {object} args - { studyData, priorStudyData, activeStackFirstId }
 * @returns {Array} stacks, excluding the one already displayed
 */
export const selectFusableSeries = ({ studyData, priorStudyData, activeStackFirstId }) => {
  const studies =
    Array.isArray(priorStudyData) && priorStudyData.length > 0
      ? priorStudyData
      : [studyData];

  const owning =
    (activeStackFirstId &&
      studies.find((study) =>
        study?.stacks?.some((stack) => stack?.imageIds?.[0] === activeStackFirstId)
      )) ||
    studies.find((study) => study?.stacks?.length) ||
    null;

  return (owning?.stacks ?? []).filter((stack) => {
    if (activeStackFirstId && stack?.imageIds?.[0] === activeStackFirstId) return false;
    // A structured report, presentation state or encapsulated document has no
    // pixel data to colorize. They sit in `stacks` alongside image series and
    // render as documents, so without this they are offered for fusion and the
    // attempt fails on whatever their imageIds point at.
    const modality = (stack?.seriesModality || stack?.modality || "").toUpperCase();
    if (NON_FUSABLE_MODALITIES.has(modality)) return false;
    return (stack?.imageIds?.length ?? 0) > 0;
  });
};

/**
 * Apply PET/CT fusion by overlaying a PET series image onto the active CT viewport.
 *
 * @param {object} viewport - The CT StackViewport to overlay PET onto
 * @param {string[]} petImageIds - Array of PET series imageIds (DICOM URIs)
 * @param {string} colormapName - VTK.js colormap preset name
 * @param {number} opacity - Overlay opacity 0-1
 * @param {object} [options]
 * @param {object} [options.baseImage] - The base image to fuse against, when it
 *   is known but not yet the viewport's current one. Passing it lets the overlay
 *   be rebuilt during a slice change rather than after it — see the note below.
 * @returns {{ok: boolean, reason?: string}} `reason` names the failure, so the
 *   caller can say which one it was rather than reporting them all alike.
 */
export const applyPETFusion = async (
  viewport,
  petImageIds,
  colormapName,
  opacity = 0.5,
  { baseImage } = {}
) => {
  try {
    if (!viewport || !petImageIds || petImageIds.length === 0) {
      console.warn("[PET Fusion] Missing viewport or PET imageIds");
      return { ok: false, reason: "no-series" };
    }

    const ticket = (_applyTickets.get(viewport.id) ?? 0) + 1;
    _applyTickets.set(viewport.id, ticket);

    // Base (displayed) geometry — the overlay has to land on exactly this plane.
    const baseImageData = viewport.getImageData();
    const baseMeta = baseImage ? utilities.getImageDataMetadata?.(baseImage) : null;
    if (!baseMeta && !baseImageData) {
      console.warn("[PET Fusion] No base image data on viewport");
      return { ok: false, reason: "no-base-image" };
    }

    const baseDirection = baseMeta?.direction ?? baseImageData.direction;
    const baseOrigin = baseMeta?.origin ?? baseImageData.origin ?? [0, 0, 0];
    const baseDims = baseMeta?.dimensions ?? baseImageData.dimensions;
    // getImageDataMetadata reports an EPSILON z-spacing for a single slice, so
    // the through-plane value has to come from the viewport either way.
    const inPlaneSpacing = baseMeta?.spacing ?? baseImageData.spacing ?? [1, 1, 1];
    const baseSpacing = [
      inPlaneSpacing[0],
      inPlaneSpacing[1],
      baseImageData?.spacing?.[2] || 1,
    ];
    const normal =
      baseDirection?.length >= 9
        ? [baseDirection[6], baseDirection[7], baseDirection[8]]
        : [0, 0, 1];

    const baseImageId = baseImage?.imageId ?? viewport.getCurrentImageId?.();
    const basePlane = baseImageId
      ? metaData.get("imagePlaneModule", baseImageId)
      : null;
    // getImageData().origin is the displayed slice's imagePositionPatient, so it
    // stands in for the image plane module whenever that lookup comes up empty.
    const baseLocation = _dot(
      Array.isArray(basePlane?.imagePositionPatient)
        ? basePlane.imagePositionPatient
        : baseOrigin,
      normal
    );

    // Everything from here to the actor update stays synchronous whenever the
    // measurements and both slices are already in hand.
    const profile =
      _seriesProfileCache.get(_profileKey(petImageIds, normal)) ??
      (await _getSeriesProfile(
        petImageIds,
        normal,
        basePlane?.frameOfReferenceUID ?? baseImageData?.metadata?.FrameOfReferenceUID
      ));

    // Pair the two series by patient position, not by array index — they do not
    // have the same slice count or spacing. Index pairing is the fallback for
    // when the overlay series cannot be located in the base's patient space.
    const canLocateSlices =
      profile.sharesPatientSpace &&
      profile.locationStep != null &&
      baseLocation != null;

    const exactIndex = canLocateSlices
      ? (baseLocation - profile.locationOrigin) / profile.locationStep
      : viewport.getCurrentImageIdIndex?.() ?? 0;

    const lastIndex = petImageIds.length - 1;
    const lowerIndex = Math.max(0, Math.min(Math.floor(exactIndex), lastIndex));
    const upperIndex = Math.min(lowerIndex + 1, lastIndex);
    // Only meaningful when the two series are registered; index pairing has no
    // sub-slice position to interpolate along.
    const blend = canLocateSlices
      ? Math.max(0, Math.min(exactIndex - lowerIndex, 1))
      : 0;

    const petImageId = petImageIds[lowerIndex];
    if (!petImageId) {
      console.warn("[PET Fusion] No PET image at index", lowerIndex);
      return { ok: false, reason: "no-overlay-slice" };
    }

    // The overlay slices bracketing the base plane. Read at Interaction
    // priority: these are what the user is waiting to see, not background work.
    const readyImages = _imagesFor(petImageIds);
    const petImage =
      readyImages.get(petImageId) ??
      (await _readOverlayImage(petImageIds, petImageId, Enums.RequestType.Interaction));
    if (!petImage) {
      console.warn("[PET Fusion] Failed to load PET image");
      return { ok: false, reason: "overlay-unreadable" };
    }

    const upperImageId =
      blend > 0 && upperIndex !== lowerIndex ? petImageIds[upperIndex] : null;
    const upperImage = upperImageId
      ? readyImages.get(upperImageId) ??
        (await _readOverlayImage(
          petImageIds,
          upperImageId,
          Enums.RequestType.Interaction
        ).catch(() => null))
      : null;

    // The slice being drawn is in hand; warm the rest so the next one is too.
    _warmOverlaySeries(petImageIds, lowerIndex);

    // Overtaken while this slice was being read — see _applyTickets. One check
    // is enough because everything from here to the actor update is
    // synchronous; keep it that way, or this needs a second one further down.
    if (_applyTickets.get(viewport.id) !== ticket) return { ok: true, superseded: true };

    // Get PET pixel data (grayscale, 16-bit signed/unsigned)
    const petPixelData = petImage.getPixelData();
    const width = petImage.columns || petImage.width;
    const height = petImage.rows || petImage.height;
    const numPixels = width * height;

    let upperPixelData = null;
    let upperRescale = null;
    if (upperImage && upperImage !== petImage) {
      const candidate = upperImage.getPixelData?.();
      if (candidate?.length === petPixelData.length) {
        upperPixelData = candidate;
        upperRescale = _rescaleOf(upperImage);
      }
    }

    // Colorize PET pixels into an RGBA buffer, against the series-wide range so
    // a given uptake keeps the same colour from slice to slice.
    const lut = _getColormapLUT(colormapName);
    const { slope, intercept } = _rescaleOf(petImage);
    const low = profile.windowLow;
    const range = profile.windowHigh - profile.windowLow || 1;
    const rgbaData = new Uint8Array(numPixels * 4);
    const alphaScale = opacity * 255;

    for (let i = 0; i < numPixels; i++) {
      let value = petPixelData[i] * slope + intercept;
      if (upperPixelData) {
        const upper = upperPixelData[i] * upperRescale.slope + upperRescale.intercept;
        value += (upper - value) * blend;
      }
      let normalized = (value - low) / range;
      normalized = normalized < 0 ? 0 : normalized > 1 ? 1 : normalized;

      const entry = ((normalized * 255) | 0) * 3;
      const offset = i * 4;
      rgbaData[offset] = lut[entry];         // R
      rgbaData[offset + 1] = lut[entry + 1]; // G
      rgbaData[offset + 2] = lut[entry + 2]; // B
      // Opacity has to be baked in here: the 4-component path of vtk.js's image
      // shader emits the texture's own alpha and never reads the actor's
      // opacity, so setting it on the actor would do nothing.
      rgbaData[offset + 3] = Math.round(normalized * alphaScale); // A
    }

    // ---- Overlay placement -------------------------------------------------
    const petMeta = utilities.getImageDataMetadata?.(petImage);
    const petImagePosition = petMeta?.origin;
    const petSpacing = petMeta?.spacing;
    const orientationMatches =
      Array.isArray(petMeta?.direction) &&
      baseDirection?.length >= 6 &&
      petMeta.direction
        .slice(0, 6)
        .every((cosine, i) => Math.abs(cosine - baseDirection[i]) < DIRECTION_EPSILON);

    let overlayOrigin;
    let overlaySpacing;

    if (
      profile.sharesPatientSpace &&
      profile.located &&
      orientationMatches &&
      Array.isArray(petImagePosition) &&
      Number.isFinite(petSpacing?.[0]) &&
      Number.isFinite(petSpacing?.[1])
    ) {
      // Project the overlay's origin onto the base slice's plane, removing only
      // the out-of-plane component and leaving its in-plane position intact.
      const offPlane = _dot(
        [
          petImagePosition[0] - baseOrigin[0],
          petImagePosition[1] - baseOrigin[1],
          petImagePosition[2] - baseOrigin[2],
        ],
        normal
      );
      overlayOrigin = [
        petImagePosition[0] - offPlane * normal[0],
        petImagePosition[1] - offPlane * normal[1],
        petImagePosition[2] - offPlane * normal[2],
      ];
      // getImageDataMetadata reports an EPSILON z-spacing for a single slice,
      // so take the through-plane spacing from the base instead.
      overlaySpacing = [petSpacing[0], petSpacing[1], baseSpacing[2] || 1];
    } else {
      // No shared patient space, or a rotated overlay: stretch it over the base
      // image's footprint. Not anatomically registered, but it keeps the
      // overlay on screen instead of dropping it entirely.
      overlayOrigin = baseOrigin;
      overlaySpacing = [
        ((baseDims?.[0] || width) * (baseSpacing[0] || 1)) / width,
        ((baseDims?.[1] || height) * (baseSpacing[1] || 1)) / height,
        baseSpacing[2] || 1,
      ];
    }

    // Create VTK image data for the RGBA overlay
    const scalarArray = vtkDataArrayFactory.newInstance({
      name: "PETFusionPixels",
      numberOfComponents: 4,
      values: rgbaData,
    });

    const fusionImageData = vtkImageDataFactory.newInstance();
    fusionImageData.setDimensions([width, height, 1]);
    fusionImageData.setSpacing(overlaySpacing);
    fusionImageData.setDirection(baseDirection);
    fusionImageData.setOrigin(overlayOrigin);
    fusionImageData.getPointData().setScalars(scalarArray);

    // Try in-place mapper update when the overlay actor already exists.
    // This avoids the remove → add cycle on every scroll, preventing flicker.
    const existingEntry = viewport.getActor?.(PET_FUSION_ACTOR_UID);
    if (existingEntry?.actor) {
      const mapper = existingEntry.actor.getMapper?.();
      if (mapper?.setInputData) {
        mapper.setInputData(fusionImageData);
        mapper.modified?.();
        viewport.render();
        return { ok: true };
      }
      // In-place update unavailable — fall back to remove/re-add
      viewport.removeActors([PET_FUSION_ACTOR_UID]);
    }

    // First application: create actor and add as overlay
    const actor = viewport.createActorMapper(fusionImageData);
    if (actor) {
      // Opacity already lives in the per-pixel alpha above; leave the actor
      // fully opaque so the slider is not applied a second time on top of it.
      actor.getProperty?.()?.setOpacity?.(1);
    }

    viewport.addActor({ uid: PET_FUSION_ACTOR_UID, actor, slabThickness: 1 });
    viewport.render();

    return { ok: true };
  } catch (error) {
    console.error("[PET Fusion] Error applying fusion:", error);
    return { ok: false, reason: "overlay-unreadable" };
  }
};

/**
 * Update the opacity of an existing PET fusion overlay without reloading.
 *
 * Currently unused, and it cannot stand in for re-applying fusion: applyPETFusion
 * bakes opacity into the overlay's per-pixel alpha because the StackViewport GPU
 * path ignores actor-level opacity for overlay actors. Wiring this up in place of
 * a re-apply would leave the slider with no visible effect.
 *
 * @param {object} viewport - The CT StackViewport with the PET overlay
 * @param {number} opacity - New opacity value 0-1
 * @returns {boolean} true if opacity was updated
 */
export const updatePETFusionOpacity = (viewport, opacity) => {
  try {
    const actorEntry = viewport.getActor?.(PET_FUSION_ACTOR_UID);
    if (!actorEntry?.actor) return false;

    const property = actorEntry.actor.getProperty?.();
    if (property?.setOpacity) {
      property.setOpacity(opacity);
      viewport.render();
      return true;
    }
    return false;
  } catch (error) {
    console.error("[PET Fusion] Error updating opacity:", error);
    return false;
  }
};

/**
 * Remove the PET fusion overlay from a viewport.
 *
 * @param {object} viewport - The CT StackViewport to remove PET overlay from
 * @returns {boolean} true if fusion was removed
 */
export const removePETFusion = (viewport) => {
  try {
    // Anything still in flight for this viewport must not re-add the overlay.
    _applyTickets.set(viewport.id, (_applyTickets.get(viewport.id) ?? 0) + 1);
    if (viewport.getActor?.(PET_FUSION_ACTOR_UID)) {
      viewport.removeActors([PET_FUSION_ACTOR_UID]);
      viewport.render();
      return true;
    }
    return false;
  } catch (error) {
    console.error("[PET Fusion] Error removing fusion:", error);
    return false;
  }
};

/**
 * Apply PET/CT fusion to all provided viewports.
 *
 * @param {Array} viewports - Array of CT StackViewport instances
 * @param {string[]} petImageIds - PET series image IDs
 * @param {string} colormapName - VTK.js colormap preset name
 * @param {number} opacity - Overlay opacity 0-1
 * @returns {number} count of viewports with fusion applied
 */
export const applyPETFusionToViewports = async (viewports, petImageIds, colormapName, opacity = 0.5) => {
  let count = 0;
  for (const viewport of viewports) {
    if (isStackViewport(viewport)) {
      const outcome = await applyPETFusion(viewport, petImageIds, colormapName, opacity);
      if (outcome?.ok) count++;
    }
  }
  return count;
};

/**
 * Remove PET fusion from all provided viewports.
 *
 * @param {Array} viewports - Array of viewport instances
 * @returns {number} count of viewports where fusion was removed
 */
export const removePETFusionFromViewports = (viewports) => {
  let count = 0;
  for (const viewport of viewports) {
    const success = removePETFusion(viewport);
    if (success) count++;
  }
  return count;
};

/**
 * Update PET fusion opacity on all provided viewports.
 *
 * @param {Array} viewports - Array of viewport instances
 * @param {number} opacity - New opacity 0-1
 * @returns {number} count of updated viewports
 */
export const updatePETFusionOpacityOnViewports = (viewports, opacity) => {
  let count = 0;
  for (const viewport of viewports) {
    const success = updatePETFusionOpacity(viewport, opacity);
    if (success) count++;
  }
  return count;
};
